import { useEffect, useRef } from 'react';

import { StudentGroupApi, StudentGroupPartnerSortField, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { ContentDeliverable, License, LicenseReturnable, TransactionApi } from 'apis/TransactionApi';
import { getLowestLicenseAmount, getMaxLicenseAmount, unlimitedUses } from 'utils/objects';
import { GMetrixComboBoxList } from 'components/GMetrixComboBoxList/GMetrixComboBoxList';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GroupGMetrixTable } from 'components/GroupGMetrixTable/GroupGMetrixTable';
import GMetrixDialogAlert from 'components/GMetrixDialogAlert/GMetrixDialogAlert';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { copyToClipboard, GenerateUUID, getFormattedDate } from 'utils';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { Card, Container, Form, Modal, Row } from 'react-bootstrap';
import { FindContent } from 'components/FindContent/FindContent';
import { currentLookupUrl, getStringFromURL } from 'utils/other';
import NewGroupForm from 'components/NewGroupForm/NewGroupForm';
import { Instructor, InstructorApi } from 'apis/InstructorApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { ToolTip } from 'components/ToolTip/ToolTip';
import { useSuperState, useUrlFilters } from 'hooks';
import { useNotification } from 'app/notifications';
import { partnerTypeNames } from 'apis/PartnerApi';
import { PageWithHeader } from 'components/layout';
import { useGroups } from 'pages/groups/context';
import { SortDescriptor } from 'utils/dataQuery';
import { KendoIntlProvider } from 'app/culture';
import { helpContent } from 'pages/groups/help';
import { useNavigate } from 'react-router-dom';
import { UsersApi } from 'apis/UsersApi';
import { useJoyride } from 'app/joyride';
import { useApp } from 'app';

import 'pages/groups/styles/main.scss';
import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

export const Groups = () => {

    const navigate = useNavigate();

    // Persist the view (filters, search, sort, pagination) in the URL query string so
    // navigating away and back restores the same view, then refetch fresh classroom rows.
    const urlFilters = useUrlFilters({
        archived: false,
        mine: false,
        searchterm: '',
        skip: 0,
        sortdir: '',
        sortfield: '',
        take: 15
    });

    const [state, setState] = useSuperState({
        codeIdentifier: '',
        currentClassroom: undefined as StudentGroupWithDetails | undefined,
        currentClassroomStep: 0,
        expiringMessage: '',
        expiringMessageLonger: '',
        filtersInitialized: false,
        groupName: '',
        groups: [] as StudentGroupWithDetails[],
        instructors: [] as Instructor[],
        isLimited: false,
        licenseExpire: false,
        licenses: [] as License[],
        licensesExpiring: [] as License[],
        loadingGridData: true,
        maxStudents: 0,
        maxUses: 0,
        newClassroomData: undefined as ContentDeliverable[] | undefined,
        paginationSkip: urlFilters.initial.skip,
        paginationTake: urlFilters.initial.take,
        searchTerm: urlFilters.initial.searchterm,
        selectedGroups: [] as StudentGroupWithDetails[],
        selectedInstructors: [] as Instructor[],
        serverTotalCount: 0,
        showArchived: urlFilters.initial.archived,
        showDuplicateClassroomModal: false,
        showExpiringLicenseNotice: false,
        showMyClassrooms: urlFilters.initial.mine,
        showNewClassroomModal: false,
        sortDir: urlFilters.initial.sortdir,
        sortField: urlFilters.initial.sortfield as StudentGroupPartnerSortField | ''
    });

    const { isSystemMode, selectedPartner, userId } = useApp();
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { filtersInitialized, licenses, paginationSkip, paginationTake, searchTerm, showArchived,
        showMyClassrooms, sortDir, sortField } = state;

    const { joyrideTours, loadCurrentTour } = useJoyride();
    const { permissions } = useGroups();
    const { formatMessage, locale } = useIntl();
    const { catchWithToast, createToast } = useNotification();

    const gmetrixAlertRef = useRef<any>();

    const dateDiff = (dateEarlier: Date, dateLater: Date) => {

        const one_day = 1000 * 60 * 60 * 24;
        return Math.round((dateLater.getTime() - dateEarlier.getTime()) / one_day);

    };

    const checkLicenses = async (licenseObj: LicenseReturnable) => {

        let expireLicenseMessageLonger = '';
        let expireLicenseMessage = '';
        const licensesToWarn: License[] = [];
        licenseObj.licenses.forEach(license => {

            if (getLowestLicenseAmount(license) === unlimitedUses) return;

            if (dateDiff(new Date(), new Date(license.OrderExpireDate)) <= 30) {

                expireLicenseMessage = formatMessage({ id: 'your_license_is_about_to_expire.c278d41f', defaultMessage: 'Your License is about to expire' });
                expireLicenseMessageLonger = formatMessage({ id: 'one_or_more_of_your_licenses_is_about_to.4e0936b4', defaultMessage: 'One or more of your licenses is about to expire. You may want to renew them.' });
                licensesToWarn.push(license);

            } else if (((getLowestLicenseAmount(license) / getMaxLicenseAmount(license)) * 100) <= 10) {

                expireLicenseMessage = formatMessage({ id: 'your_license_is_nearly_out_of_uses.cab5eca3', defaultMessage: 'Your license is nearly out of uses.' });
                expireLicenseMessageLonger = formatMessage({ id: 'one_or_more_of_your_licenses_is_nearly_o.cb1e42a5', defaultMessage: 'One or more of your licenses is nearly out of uses. You may want to renew them.' });
                licensesToWarn.push(license);

            }

        });

        if (licensesToWarn.length) {

            setState({
                expiringMessage: expireLicenseMessage,
                expiringMessageLonger: expireLicenseMessageLonger,
                licensesExpiring: licensesToWarn,
                showExpiringLicenseNotice: true
            });

        }

    };

    // Loads the license + instructor data the New/Duplicate forms and expiring-license notice need.
    const loadGlobalData = async () => {

        const [licenseObj, allInstructors] = await Promise.all([
            TransactionApi.GetAllLicenses().catch(catchWithToast(formatMessage({ id: 'failed_to_load_license_data.ccdc93b9', defaultMessage: 'Failed to load license data.' }))),
            InstructorApi.GetAll().catch(catchWithToast(formatMessage({ id: 'failed_to_load_instructor_data.15f58828', defaultMessage: 'Failed to load instructor data.' })))
        ]);

        if (allInstructors) {

            const currentInstructor = allInstructors.find(i => i.Id === userId);
            if (currentInstructor) {

                setState({ selectedInstructors: [...state.selectedInstructors, currentInstructor] });

            }
            setState({ instructors: allInstructors });

        }

        if (licenseObj) {

            setState({ licenses: licenseObj.licenses });

            if (permissions.ExpiringLicense) {

                checkLicenses(licenseObj);

            }

        }

        // this means create mode
        if (getStringFromURL('create') === 'true') {

            setState({ showNewClassroomModal: true });

        }

    };

    const reloadGroupTableData = async () => {

        fetchGroupsPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm, showArchived, showMyClassrooms);

    };

    const fetchGroupsPaged = async (
        skip = paginationSkip,
        take = paginationTake,
        sField = sortField,
        sDir = sortDir,
        search = searchTerm,
        archived = showArchived,
        myClassrooms = showMyClassrooms
    ) => {

        // In system mode (no testing center selected) a search term is required before querying
        // classrooms across all partners, mirroring the system-level Students view.
        if (isSystemMode && !search.trim()) {

            setState({ groups: [], loadingGridData: false, serverTotalCount: 0 });
            return;

        }

        setState({ loadingGridData: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await StudentGroupApi.GetAllPaged(
                page,
                take,
                sField,
                sDir,
                search,
                archived,
                myClassrooms
            );
            setState({ groups: result.Items, serverTotalCount: result.TotalCount });
            urlFilters.write({
                archived,
                mine: myClassrooms,
                searchterm: search,
                skip,
                sortdir: sDir,
                sortfield: sField,
                take
            });

        } catch (error) {

            console.error(error);
            const message = typeof error === 'string' ? error : (error as any)?.message || (error as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_classroom_data_please_ref.d544c752', defaultMessage: 'Failed to load classroom data. Please refresh the page.' }), 'danger');

            }

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchGroupsPaged(skip, take, sortField, sortDir, searchTerm, showArchived, showMyClassrooms);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as StudentGroupPartnerSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchGroupsPaged(0, paginationTake, newSortField, newSortDir, searchTerm, showArchived, showMyClassrooms);

    };

    const handleFilterChange = (filters: { showArchived: boolean; showMyClassrooms: boolean }) => {

        // The table emits its filters once on mount and again whenever the user toggles one.
        // On the first (mount) emission, restore the previous page/sort/search and refetch the
        // exact view the user left; on later emissions a filter actually changed, so reset to
        // the first page.
        if (!filtersInitialized) {

            setState({ filtersInitialized: true, showArchived: filters.showArchived, showMyClassrooms: filters.showMyClassrooms });
            fetchGroupsPaged(paginationSkip, paginationTake, sortField, sortDir, searchTerm, filters.showArchived, filters.showMyClassrooms);
            return;

        }

        setState({ paginationSkip: 0, showArchived: filters.showArchived, showMyClassrooms: filters.showMyClassrooms });
        fetchGroupsPaged(0, paginationTake, sortField, sortDir, searchTerm, filters.showArchived, filters.showMyClassrooms);

    };

    const checkLicenseExpire = () => {

        if (state.licenseExpire) {

            // run the DB schema to never check again
            UsersApi.SetPagePreference(
                'http://schemas.gmetrix.net/claims/Admin/Preference/PagePreference/ExpiringLicense',
                'Hide'
            );

        }
        setState({ showExpiringLicenseNotice: false });
        UsersApi.SetExpiringLicenseSession(false);

    };

    const getNewClassroomStep = () => {

        if (state.currentClassroomStep === 0) {

            return (
                <FindContent
                    onSuccess={contentData => {

                        if (contentData !== undefined) {

                            setState({ currentClassroomStep: 1, newClassroomData: { ...state.newClassroomData, ...contentData } });

                        }

                    }
                    }
                    ActionsWrapper={Modal.Footer}
                    ContentWrapper={Modal.Body}
                    onClose={() => checkCancelStep()}
                    selectedContent={state.newClassroomData && (state.newClassroomData as any).content}
                    variant="picker"
                />
            );

        } else {

            return (
                <NewGroupForm
                    onSuccess={classroomId => {

                        setState({ currentClassroomStep: 0, newClassroomData: undefined, showNewClassroomModal: false });
                        const url = new URL(window.location.href);
                        const search_params = url.searchParams;
                        search_params.delete('create');
                        url.search = search_params.toString();
                        window.history.replaceState(null, '', url);
                        navigate('/classrooms/' + classroomId, { state: { from: currentLookupUrl() } });

                    }
                    }
                    ActionsWrapper={Modal.Footer}
                    ContentWrapper={Modal.Body}

                    newClassroomData={state.newClassroomData}
                    onClose={() => checkCancelStep()}
                />
            );

        }

    };

    const checkCancelStep = () => {

        if (state.currentClassroomStep === 1) {

            setState({ currentClassroomStep: 0 });

        } else {

            // create new classroom
            setState({ newClassroomData: undefined, showNewClassroomModal: false });

        }

    };

    const handleDuplicateClassroom = async (groupItem: StudentGroupWithDetails | undefined) => {

        // ask for new details (name, identifier, etc)
        if (groupItem === undefined) {

            createToast(formatMessage({ id: 'no_classroom_selected_for_duplication.3d3f803f', defaultMessage: 'No classroom selected for duplication.' }), 'danger');
            return;

        }
        try {

            // once ready duplicate the transaction
            const transactions = await StudentGroupApi.GetTransactionsFromGroup(groupItem.Id);
            if (transactions && transactions.length > 0) {

                const duplicateProcess = await TransactionApi.Duplicate(transactions[0].Id);
                if (duplicateProcess && duplicateProcess.Done && duplicateProcess.Id) {

                    // create a new classroom
                    const newClassroom = await StudentGroupApi.CreateEditStudentGroup(0, groupItem.Name + ' DUPLICATED', groupItem.Description ?? '', groupItem.MaxStudents ?? 0, true, groupItem.Control?.split('-')[0] ?? GenerateUUID());
                    if (newClassroom.Done) {

                        // assign that classroom to the new transaction
                        await TransactionApi.AddGroupToTransaction([duplicateProcess.Id], newClassroom.ExtraData);

                        // copy all instructors
                        const allInstructorIds = Array.isArray(groupItem.Instructors)
                            ? groupItem.Instructors.map(instructor => instructor.Id)
                            : (await InstructorApi.Get(groupItem.Id)).map(instructor => instructor.Id);
                        await StudentGroupApi.AddEditInstructorByGroup([newClassroom.ExtraData], allInstructorIds, false);

                    } else {

                        throw new Error(newClassroom.Message || formatMessage({ id: 'failed_to_create_new_classroom.b90bc595', defaultMessage: 'Failed to create new classroom' }));

                    }

                    createToast(formatMessage({ id: 'classroom_was_duplicated_successfully.631a6b3d', defaultMessage: 'Classroom was duplicated successfully.' }), 'success');

                    // reload lookup table and open the new classroom
                    reloadGroupTableData();
                    navigate('/classrooms/' + newClassroom.ExtraData);

                } else {

                    const errorMsg = duplicateProcess?.Message || formatMessage({ id: 'failed_to_duplicate_access_code.a9a0c706', defaultMessage: 'Failed to duplicate access code' });
                    createToast(errorMsg, 'danger');

                }

            } else {

                createToast(formatMessage({ id: 'no_access_code_is_attached_to_this_class.dc948640', defaultMessage: 'No access code is attached to this classroom.' }), 'danger');

            }

        } catch (error) {

            console.error(error);
            const errorMessage = error instanceof Error ? error.message : formatMessage({ id: 'an_error_occurred_while_duplicating_the.83bb182b', defaultMessage: 'An error occurred while duplicating the classroom.' });
            createToast(errorMessage, 'danger');

        } finally {

            setState({ currentClassroom: undefined });

        }

    };

    const getDuplicateForm = () => (
        <Container fluid className="details" >
            <GMetrixInput
                autoFocus
                required
                onChange={e => {

                    setState({ groupName: e.target.value });

                }}
                className="mb-2"
                label={formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })}
                maxLength={50}
                placeholder={formatMessage({ id: 'classroom_name_1.2b01eefa', defaultMessage: 'Classroom Name 1' })}
                value={state.groupName}
            />

            <GMetrixInput
                onChange={e => {

                    setState({ codeIdentifier: e.target.value });

                }}
                className="mb-2"
                infoTitle={formatMessage({ id: 'the_identifier_is_used_to_identify_the_c.88ee9066', defaultMessage: 'The identifier is used to identify the code. Additional random characters will be added when the access code is generated.' })}
                label={formatMessage({ id: 'identifier_optional.6ca7cdc8', defaultMessage: 'Identifier (optional)' })}
                maxLength={8}
                placeholder={formatMessage({ id: 'identifier1.8e2d1f7f', defaultMessage: 'Identifier1' })}
                value={state.codeIdentifier}
            />
            <Form.Group>

                <Form.Label className="m-0 fw-bold">
                    <FormattedMessage defaultMessage="Number of Students Allowed" id="number_of_students_allowed.41cbfb2c" />
                </Form.Label>
                <ToolTip
                    content={`This setting controls the maximum number of students who can join this Classroom. This license currently has ${state.maxUses} seats in total.
                        No Limit:This Classroom can use as many seats as are available in the license pool. Availability may change as other Classrooms sharing the license enroll students.
                        Limit to: Enrollments for this Classroom will stop at the number you set, even if more seats are available on the license.`} className="mb-1"
                />
                <div>

                    <GMetrixCheckInput
                        onChange={() => {

                            setState({ isLimited: false, maxStudents: 0 });

                        }} checked={!state.isLimited} label={formatMessage({ id: 'no_limit.059f752f', defaultMessage: 'No limit' })} subLabel="(up to license total)" type={'radio'}
                    />

                    {/* this margin cancels out the input margin for keeping it in line */}
                    <div className="d-flex justify-content start" style={{ marginTop: '15px' }}>

                        <GMetrixCheckInput
                            label={(
                                <div className="d-flex text-nowrap" >
                                    <FormattedMessage defaultMessage="Limit to" id="limit_to.afeb0f97" />

                                    <GMetrixInput
                                        onChange={e => {

                                            const maxValue: number = parseInt(e.target.value);
                                            if (maxValue > state.maxUses) {

                                                setState({ maxStudents: state.maxUses });

                                            } else if (maxValue === 0) {

                                                setState({ maxStudents: 1 });

                                            } else {

                                                setState({ maxStudents: maxValue });

                                            }

                                        }}
                                        className="mx-2"
                                        disabled={!state.isLimited}
                                        max={state.maxUses}
                                        min={1}
                                        style={{ marginTop: '-15px', width: 100 }}
                                        type="number"
                                        value={state.maxStudents}
                                    />
                                    students
                                </div>
                            )}
                            onChange={() => {

                                setState({ isLimited: true, maxStudents: 1 });

                            }}
                            checked={state.isLimited}
                            type={'radio'}
                        />
                    </div>
                </div>
            </Form.Group>

            <GMetrixComboBoxList
                className="mb-2"
                dataList={state.instructors}
                displayNameProp="FirstName"
                displayNameSecondaryProp="LastName"
                infoTitle={formatMessage({ id: 'instructors_are_users_who_can_manage_cla.1dd0e940', defaultMessage: 'Instructors are users who can manage Classrooms, assign content, edit settings, and manually add students.' })}
                label={formatMessage({ id: 'instructors.24923680', defaultMessage: 'Instructors' })}
                selectedListItems={state.selectedInstructors}
                setDataList={v => setState({ selectedInstructors: v })}
            />

        </Container>
    );

    const handleNewClassroom = () => setState({ currentClassroomStep: 0, showNewClassroomModal: true });

    useEffect(() => {

        loadGlobalData();

    }, []);

    // Debounce search term changes for server-side pagination
    const isInitialSearchRef = useRef(true);
    useEffect(() => {

        // The first server call is kicked off by the table filters on mount.
        // Skip the first debounced run to avoid duplicate requests.
        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }

        if (!isSystemMode || searchTerm.trim()) {

            setState({ loadingGridData: true });

        }

        searchDebounceRef.current = setTimeout(() => {

            setState({ paginationSkip: 0 });
            fetchGroupsPaged(0, paginationTake, sortField, sortDir, searchTerm, showArchived, showMyClassrooms);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]);

    useEffect(() => {

        if (joyrideTours.length > 0) {

            loadCurrentTour('PageLoad');

        }

    }, [joyrideTours]);

    const noLicensedContent = !(licenses.length > 0);
    const managedPartnerTypeName = partnerTypeNames[(selectedPartner?.type ?? 0) - 1] ?? 'Testing Center';

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <PageWithHeader
                        headerConfig={{
                            cta: {
                                disabled: !permissions.Create || noLicensedContent,
                                label: (
                                    <div className={'d-inline-flex align-items-center gap-2'}>
                                        <i className={'fa-solid fa-plus'} />
                                        <span>
                                            <FormattedMessage defaultMessage="New Classroom" id="new_classroom.02c85b91" />
                                        </span>
                                    </div>
                                ),
                                onClick: handleNewClassroom,
                                popoverContent: formatMessage({ id: 'this_partnertype_has_no_licensed_content.6ce0a50a', defaultMessage: 'This {partnerType} has no licensed content available, so a classroom cannot be created yet.' }, { partnerType: managedPartnerTypeName }),
                                renderPopover: noLicensedContent
                            },
                            helpContent: helpContent,
                            title: formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' })
                        }}
                    >

                        <div className="groups d-flex flex-column gap-3">

                            <Card body>
                                <GroupGMetrixTable
                                    manuallyRenderGroups
                                    serverSidePagination
                                    toggleColumns
                                    columnData={[
                                        {
                                            field: 'Name',
                                            title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })
                                        },
                                        ...(isSystemMode ? [{
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {dataItem.Partner?.Name}
                                                </td>
                                            ),
                                            field: 'Partner.Name',
                                            sortField: 'PartnerName',
                                            title: formatMessage({ id: 'partner.cb022868', defaultMessage: 'Partner' })
                                        }] : []),
                                        {
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {dataItem.Control}
                                                </td>
                                            ),
                                            field: 'Identifier',
                                            sortField: 'Control',
                                            title: formatMessage({ id: 'identifier.45eac7f9', defaultMessage: 'Identifier' })
                                        },
                                        {
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {dataItem.StudentCount}
                                                </td>
                                            ),
                                            field: 'StudentCount',
                                            title: formatMessage({ id: 'student_count.4d7f96f3', defaultMessage: 'Student Count' })
                                        },
                                        {
                                            field: 'Instructors',
                                            title: formatMessage({ id: 'instructors.24923680', defaultMessage: 'Instructors' })

                                        },
                                        {
                                            field: 'DateCreate',
                                            sortField: 'DateCreated',
                                            title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' })
                                        },
                                        {
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {getFormattedDate(new Date(dataItem.ExpirationDate))}
                                                </td>
                                            ),
                                            field: 'ExpirationDate',
                                            sortField: 'ExpirationDate',
                                            title: formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })
                                        },
                                        {
                                            cell: ({ dataItem }) => (
                                                <td>
                                                    {dataItem.Control}
                                                </td>
                                            ),
                                            field: 'Control',
                                            title: formatMessage({ id: 'code.cc651d9c', defaultMessage: 'Code' })
                                        }
                                    ]}
                                    detailsDropdownItems={dataItem => [
                                        {
                                            action: () => {

                                                try {

                                                    if (!dataItem.Control) {

                                                        createToast(formatMessage({ id: 'no_classroom_code_available_to_copy.68c50e02', defaultMessage: 'No classroom code available to copy.' }), 'danger');
                                                        return;

                                                    }
                                                    copyToClipboard(dataItem.Control);
                                                    createToast(formatMessage({ id: 'classroom_code_code_has_been_successfull.0d761b3f', defaultMessage: 'Classroom code {code} has been successfully copied!' }, { code: dataItem.Control }), 'success');

                                                } catch (error) {

                                                    console.error(error);
                                                    createToast(formatMessage({ id: 'failed_to_copy_classroom_code.52e16d8d', defaultMessage: 'Failed to copy classroom code.' }), 'danger');

                                                }

                                            }, iconClass: 'fa-cabinet-filing text-warning', text: formatMessage({ id: 'copy_code.b4e097d4', defaultMessage: 'Copy Code' })
                                        },
                                        {
                                            action: async () => {

                                                try {

                                                    await handleDuplicateClassroom(dataItem);

                                                } catch (error) {

                                                    console.error(error);
                                                    createToast(formatMessage({ id: 'failed_to_duplicate_classroom_please_try.ea3cfd5f', defaultMessage: 'Failed to duplicate classroom. Please try again.' }), 'danger');

                                                }

                                            }, iconClass: 'fa-cabinet-filing text-warning', text: formatMessage({ id: 'duplicate.9ae890dd', defaultMessage: 'Duplicate' })
                                        },
                                        {
                                            action: async () => {

                                                try {

                                                    const result = await StudentGroupApi.SetArchivedStatus(dataItem.Id, true);

                                                    if (!result.Done) {

                                                        createToast(result.Message || formatMessage({ id: 'failed_to_archive_classroom.efcdbf5b', defaultMessage: 'Failed to archive classroom.' }), 'danger');
                                                        return;

                                                    }
                                                    createToast(formatMessage({ id: 'classroom_was_archived_successfully.46f5d0f4', defaultMessage: 'Classroom was archived successfully' }), 'success');
                                                    await reloadGroupTableData();

                                                } catch (error) {

                                                    console.error(error);
                                                    createToast(formatMessage({ id: 'an_error_occurred_while_archiving_the_cl.05da7356', defaultMessage: 'An error occurred while archiving the classroom. Please try again.' }), 'danger');
                                                    setState({ loadingGridData: false });

                                                }

                                            }, hidden: dataItem.Archived, iconClass: 'fa-cabinet-filing text-warning', text: formatMessage({ id: 'archive.61ce1f3a', defaultMessage: 'Archive' })
                                        },
                                        {
                                            action: async () => {

                                                try {

                                                    const result = await StudentGroupApi.SetArchivedStatus(dataItem.Id, false);

                                                    if (!result.Done) {

                                                        createToast(result.Message || formatMessage({ id: 'failed_to_restore_classroom.e63e1336', defaultMessage: 'Failed to restore classroom.' }), 'danger');
                                                        return;

                                                    }

                                                    createToast(formatMessage({ id: 'classroom_was_restored.2f8ff279', defaultMessage: 'Classroom was restored' }), 'success');
                                                    await reloadGroupTableData();

                                                } catch (error) {

                                                    console.error(error);
                                                    createToast(formatMessage({ id: 'an_error_occurred_while_restoring_the_cl.91905bd3', defaultMessage: 'An error occurred while restoring the classroom. Please try again.' }), 'danger');
                                                    setState({ loadingGridData: false });

                                                }

                                            }, hidden: !dataItem.Archived, iconClass: 'fa-undo text-info', text: formatMessage({ id: 'restore.d528bbad', defaultMessage: 'Restore' })
                                        }
                                    ]}
                                    allowExport={false}
                                    currentUserId={userId}
                                    defaultItemsPerPage={paginationTake}
                                    groups={state.groups}
                                    initialSkip={paginationSkip}
                                    loadingGridData={state.loadingGridData}
                                    onDetailsItemClick={dataItem => navigate('/classrooms/' + dataItem.Id, { state: { from: currentLookupUrl(), title: dataItem.Name } })}
                                    onFilterChange={handleFilterChange}
                                    onSelectedChange={v => setState({ selectedGroups: v })}
                                    onServerPageChange={handleServerPageChange}
                                    onServerSortChange={handleServerSortChange}
                                    permissions={permissions}
                                    searchTerm={state.searchTerm}
                                    selectableRows={false}
                                    selectedGroups={state.selectedGroups}
                                    serverTotalCount={state.serverTotalCount}
                                    setGroups={v => setState({ groups: v })}
                                    setLoadingGridData={v => setState({ loadingGridData: typeof v === 'function' ? v(state.loadingGridData) : v })}
                                    setSearchTerm={v => setState({ searchTerm: v })}
                                />
                            </Card>

                            <GMetrixDialogAlert ref={gmetrixAlertRef} />

                            <Modal
                                onHide={() => setState({ currentClassroomStep: 0, newClassroomData: undefined, showNewClassroomModal: false })}
                                show={state.showNewClassroomModal} size="xl"
                            >
                                <Modal.Header closeButton>
                                    <Modal.Title className="w-100">
                                        <FormattedMessage defaultMessage="New Classroom" id="new_classroom.02c85b91" />
                                    </Modal.Title>
                                </Modal.Header>

                                {getNewClassroomStep()}

                            </Modal>

                            <GMetrixDialog
                                actionButtonClick={() => handleDuplicateClassroom(state.currentClassroom)}
                                actionButtonDisabled={false}
                                actionButtonText={state.currentClassroomStep === 0 ? formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' }) : formatMessage({ id: 'create.be56b47d', defaultMessage: 'Create' })}
                                closeButtonText={formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })}
                                dialogLabel={formatMessage({ id: 'duplicate_classroom.0011ae2f', defaultMessage: 'Duplicate Classroom' })}
                                onCancel={() => setState({ currentClassroom: undefined, showDuplicateClassroomModal: false })}
                                onClose={() => setState({ currentClassroom: undefined, showDuplicateClassroomModal: false })}
                                show={state.showDuplicateClassroomModal}
                                size="xl"
                            >
                                {getDuplicateForm()}
                            </GMetrixDialog>

                            <GMetrixDialog
                                showCheckBox
                                actionButtonClick={checkLicenseExpire}
                                actionButtonDisabled={false}
                                actionButtonText={formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' })}
                                checkBoxLabel={formatMessage({ id: 'dont_show_me_this_again.db957447', defaultMessage: 'Don\'t show me this again' })}
                                dialogLabel={state.expiringMessage}
                                onCancel={() => setState({ showExpiringLicenseNotice: false })}
                                setCheckBoxValue={v => setState({ licenseExpire: typeof v === 'function' ? v(state.licenseExpire) : v })}
                                show={state.showExpiringLicenseNotice}
                                showCancelButton={false}
                            >
                                <Row className="m-4 w-100">
                                    {state.expiringMessageLonger}
                                </Row>
                                <Row className="m-4">
                                    <GMetrixTable
                                        columnData={[
                                            { field: 'LicenseName', title: formatMessage({ id: 'license.506a29aa', defaultMessage: 'License' }) },
                                            { field: 'OrderExpireDateString', title: formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' }), width: 150 },
                                            {
                                                cell: (props: any) => {

                                                    // eslint-disable-next-line react/prop-types
                                                    const lowestUses = getLowestLicenseAmount(props.dataItem);
                                                    return (
                                                        <td>
                                                            {lowestUses === unlimitedUses ? 'Unlimited' : lowestUses}
                                                        </td>
                                                    );

                                                },
                                                field: 'NumAvailable',
                                                title: formatMessage({ id: 'uses_left.aae1b7d3', defaultMessage: 'Uses Left' }),
                                                width: 125
                                            }
                                        ]}
                                        rowData={state.licensesExpiring}
                                    ></GMetrixTable>
                                </Row>
                            </GMetrixDialog>

                        </div >

                    </PageWithHeader>
                </IntlProvider >
            </LocalizationProvider >
        </KendoIntlProvider >
    );

};
